import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Button,
  Card,
  Heading,
  TextField,
  TextArea,
  Badge,
  Switch,
  Table
} from '@radix-ui/themes';
import { CheckIcon, ReloadIcon, TrashIcon } from '@radix-ui/react-icons';
import { toast } from 'react-hot-toast';
import ClinicLayout from './ClinicLayout';
import OnboardingScriptEditor from './OnboardingScriptEditor';
import ConsentSettingsEditor, { ConsentLanguage } from './ConsentSettingsEditor';
import type { ConsentWording } from '../../utils/consentText';
import { clinicApi, organizationApi, organizationsAdminApi } from '../../services/api';
import { useOrganization } from '../../contexts/OrganizationContext';
import { formatPhoneForDisplay } from '../../utils/formatters';

interface ExcludedDateRow {
  exclusion: string;
  reason: string | null;
}

const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

const ALL_LANGUAGES = [
  { code: 'es-US', name: 'Spanish', native_name: 'Español' },
  { code: 'fr-FR', name: 'French', native_name: 'Français' },
  { code: 'he-IL', name: 'Hebrew', native_name: 'עברית' },
  { code: 'ar-001', name: 'Arabic', native_name: 'العربية' },
];

const ClinicProfilePage: React.FC = () => {
  const { orgId: selectedOrgId } = useOrganization();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [orgId, setOrgId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [logoPath, setLogoPath] = useState('');
  const [voicePhoneNumber, setVoicePhoneNumber] = useState('');
  const [smsPhoneNumber, setSmsPhoneNumber] = useState('');
  // PHA-1202: read-only here; Phanova admins switch it on Admin → Clinics.
  const [smsProvider, setSmsProvider] = useState('twilio');
  const [ringcentralAccount, setRingcentralAccount] = useState<string | null>(null);
  const [smsMessageTemplate, setSmsMessageTemplate] = useState('');
  // PHA-871: per-clinic SMS body for the "Share RPM readings" link.
  const [rpmReadingsSmsTemplate, setRpmReadingsSmsTemplate] = useState('');
  // PHA-1010: auto-reply to inbound patient texts. The phone here is the number a PATIENT
  // should call — the clinic's own front desk — which is neither the SMS sender nor the
  // Voice AI number, hence a separate field rather than a reuse of either.
  const [smsAutoReplyEnabled, setSmsAutoReplyEnabled] = useState(true);
  const [smsAutoReplyPhoneNumber, setSmsAutoReplyPhoneNumber] = useState('');
  const [smsAutoReplyTemplate, setSmsAutoReplyTemplate] = useState('');
  const [domain, setDomain] = useState<string | null>(null);

  const [tellihealthWebhookToken, setTellihealthWebhookToken] = useState('');
  const [webhookUrl, setWebhookUrl] = useState<string | null>(null);
  const [eventsUrl, setEventsUrl] = useState<string | null>(null);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [welcomeSmsEnabled, setWelcomeSmsEnabled] = useState(false);
  const [welcomeSmsTemplate, setWelcomeSmsTemplate] = useState('');
  // PHA-626: saved by its own editor card, not by "Save Changes".
  const [onboardingScriptTemplate, setOnboardingScriptTemplate] = useState<unknown>(null);
  // PHA-1225: also saved by its own card. English here; other languages ride on orgLanguages.
  const [consentSettings, setConsentSettings] = useState<ConsentWording>({
    consent_sms_template: null,
    consent_form_text: null,
    consent_question: null,
  });

  const [weekdaysDraft, setWeekdaysDraft] = useState<string[]>([]);
  const [excludedDates, setExcludedDates] = useState<ExcludedDateRow[]>([]);
  const [excludedDatesLoading, setExcludedDatesLoading] = useState(false);
  const [dateDraft, setDateDraft] = useState({ exclusion: '', reason: '' });

  const [orgLanguages, setOrgLanguages] = useState<Array<ConsentLanguage & {native_name: string, sms_message_template?: string}>>([]);
  const [langTemplateDrafts, setLangTemplateDrafts] = useState<Record<string, string>>({});

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await clinicApi.getSettings();
      setOrgId(data?.id ? String(data.id) : null);
      setName(data?.name ?? '');
      setLogoPath(data?.logo_path ?? '');
      setVoicePhoneNumber(data?.voice_phone_number ?? '');
      setSmsPhoneNumber(data?.sms_phone_number ?? '');
      setSmsProvider(data?.sms_provider ?? 'twilio');
      setRingcentralAccount(data?.ringcentral_credential_key ?? null);
      setSmsMessageTemplate(data?.sms_message_template ?? '');
      setRpmReadingsSmsTemplate(data?.rpm_readings_sms_template ?? '');
      setSmsAutoReplyEnabled(data?.sms_auto_reply_enabled ?? true);
      setSmsAutoReplyPhoneNumber(data?.sms_auto_reply_phone_number ?? '');
      setSmsAutoReplyTemplate(data?.sms_auto_reply_template ?? '');
      setDomain(data?.domain ?? null);
      setTellihealthWebhookToken(data?.tellihealth_webhook_token ?? '');
      setWebhookUrl(data?.tellihealth_webhook_url ?? null);
      setEventsUrl(data?.tellihealth_events_url ?? null);
      setVoiceEnabled(data?.voice_enabled ?? false);
      setWelcomeSmsEnabled(data?.welcome_sms_enabled ?? false);
      setWelcomeSmsTemplate(data?.welcome_sms_template ?? '');
      setOnboardingScriptTemplate(data?.onboarding_script_template ?? null);
      setConsentSettings({
        consent_sms_template: data?.consent_sms_template ?? null,
        consent_form_text: data?.consent_form_text ?? null,
        consent_question: data?.consent_question ?? null,
      });
      setWeekdaysDraft(data?.contact_exclusions?.weekdays ?? []);
      setExcludedDates(data?.contact_exclusions?.dates ?? []);
    } catch (err: any) {
      console.error('Failed to load clinic settings:', err);
      setError(err.message || 'Failed to load clinic settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
    // selectedOrgId is in deps so the page refetches when the user picks a
    // different clinic in the OrganizationSelector (PHA-536).
  }, [loadSettings, selectedOrgId]);

  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const data = await organizationApi.getSupportedLanguages();
        if (Array.isArray(data)) {
          const nonEnglish = data.filter((l: any) => l.code !== 'en-US');
          setOrgLanguages(nonEnglish);
          const drafts: Record<string, string> = {};
          nonEnglish.forEach((l: any) => {
            if (l.sms_message_template) {
              drafts[l.code] = l.sms_message_template;
            }
          });
          setLangTemplateDrafts(drafts);
        }
      } catch (err) {
        console.error('Failed to fetch supported languages:', err);
      }
    };
    fetchLanguages();
    // PHA-1156: selectedOrgId, like loadSettings above. With [] this ran once for
    // whichever clinic happened to be selected on mount, so switching clinics in the
    // OrganizationSelector left the previous clinic's languages and templates on
    // screen -- the same "the message is gone" symptom, one org over.
  }, [selectedOrgId]);

  const handleSaveProfile = async () => {
    if (!name.trim()) {
      toast.error('Clinic name is required');
      return;
    }

    const PHONE_REGEX = /^(\d{10}$)|(^\+[1-9]\d{1,14}$)/;
    if (voicePhoneNumber.trim() && !PHONE_REGEX.test(voicePhoneNumber.trim())) {
      toast.error('Invalid voice phone number. Use 10 digits or E.164 format (e.g. +12125551234)');
      return;
    }
    if (smsPhoneNumber.trim() && !PHONE_REGEX.test(smsPhoneNumber.trim())) {
      toast.error('Invalid SMS phone number. Use 10 digits or E.164 format (e.g. +12125551234)');
      return;
    }
    if (smsMessageTemplate.trim() && !smsMessageTemplate.includes('{link}')) {
      toast.error('SMS template must contain {link} placeholder for the survey URL');
      return;
    }
    if (rpmReadingsSmsTemplate.trim() && !rpmReadingsSmsTemplate.includes('{link}')) {
      toast.error('RPM Readings SMS template must contain {link} placeholder for the report URL');
      return;
    }
    if (smsAutoReplyPhoneNumber.trim() && !PHONE_REGEX.test(smsAutoReplyPhoneNumber.trim())) {
      toast.error('Invalid auto-reply phone number. Use 10 digits or E.164 format (e.g. +12125551234)');
      return;
    }
    if (smsAutoReplyTemplate.trim() && !smsAutoReplyTemplate.includes('{clinic_phone}')) {
      toast.error('Auto-reply template must contain the {clinic_phone} placeholder');
      return;
    }

    setSaving(true);
    try {
      await clinicApi.updateSettings({
        name: name.trim(),
        logoPath: logoPath.trim() || undefined,
        voicePhoneNumber: voicePhoneNumber.trim() || undefined,
        smsPhoneNumber: smsPhoneNumber.trim() || undefined,
        smsMessageTemplate: smsMessageTemplate.trim() || undefined,
        rpmReadingsSmsTemplate: rpmReadingsSmsTemplate.trim() || undefined,
        smsAutoReplyEnabled,
        // '' rather than undefined: undefined means "leave unchanged" to the BFF, so a
        // cleared field would silently keep quoting the old number to patients.
        smsAutoReplyPhoneNumber: smsAutoReplyPhoneNumber.trim(),
        smsAutoReplyTemplate: smsAutoReplyTemplate.trim(),
        tellihealthWebhookToken: tellihealthWebhookToken.trim(),
        voiceEnabled,
        welcomeSmsEnabled,
        welcomeSmsTemplate: welcomeSmsTemplate.trim() || undefined,
      });
      toast.success('Clinic profile updated');
      loadSettings();
    } catch (err: any) {
      console.error('Failed to update clinic profile:', err);
      toast.error(err.message || 'Failed to update clinic profile');
    } finally {
      setSaving(false);
    }
  };

  const toggleWeekday = (day: string) => {
    setWeekdaysDraft((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const saveWeekdays = async () => {
    if (!orgId) {
      toast.error('Clinic ID not available');
      return;
    }
    try {
      const res = await organizationsAdminApi.updateExcludedWeekdays(orgId, weekdaysDraft);
      if (res?.updated) {
        toast.success('Contact exclusions updated');
      } else {
        toast.error('Update did not apply');
      }
    } catch (err: any) {
      console.error('Failed to update excluded weekdays:', err);
      toast.error(err.message || 'Failed to update excluded weekdays');
    }
  };

  const refreshExcludedDates = async (targetOrgId: string) => {
    setExcludedDatesLoading(true);
    try {
      const data = await organizationsAdminApi.listExcludedDates(targetOrgId);
      setExcludedDates((data.records || []) as ExcludedDateRow[]);
    } catch (err: any) {
      console.error('Failed to load excluded dates:', err);
      toast.error(err.message || 'Failed to load excluded dates');
    } finally {
      setExcludedDatesLoading(false);
    }
  };

  const addExcludedDate = async () => {
    if (!orgId) {
      toast.error('Clinic ID not available');
      return;
    }
    if (!dateDraft.exclusion.trim()) {
      toast.error('Exclusion is required');
      return;
    }
    try {
      const res = await organizationsAdminApi.addExcludedDate(orgId, {
        exclusion: dateDraft.exclusion,
        reason: dateDraft.reason || null,
      });
      if (res?.inserted) {
        toast.success('Excluded date added');
        setDateDraft({ exclusion: '', reason: '' });
        refreshExcludedDates(orgId);
      } else {
        toast.error('Insert did not apply');
      }
    } catch (err: any) {
      console.error('Failed to add excluded date:', err);
      toast.error(err.message || 'Failed to add excluded date');
    }
  };

  const deleteExcludedDate = async (exclusion: string) => {
    if (!orgId) {
      toast.error('Clinic ID not available');
      return;
    }
    try {
      const res = await organizationsAdminApi.deleteExcludedDate(orgId, exclusion);
      if (res?.deleted) {
        toast.success('Excluded date removed');
        setExcludedDates((prev) => prev.filter((row) => row.exclusion !== exclusion));
      } else {
        toast.error('Delete did not apply');
      }
    } catch (err: any) {
      console.error('Failed to delete excluded date:', err);
      toast.error(err.message || 'Failed to delete excluded date');
    }
  };

  const handleToggleLanguage = async (lang: typeof ALL_LANGUAGES[number], enabled: boolean) => {
    try {
      if (enabled) {
        await organizationApi.upsertSupportedLanguage(lang.code);
        setOrgLanguages(prev => [...prev, { code: lang.code, name: lang.name, native_name: lang.native_name }]);
        toast.success(`${lang.name} enabled`);
      } else {
        await organizationApi.deleteSupportedLanguage(lang.code);
        setOrgLanguages(prev => prev.filter(l => l.code !== lang.code));
        setLangTemplateDrafts(prev => {
          const next = { ...prev };
          delete next[lang.code];
          return next;
        });
        toast.success(`${lang.name} disabled`);
      }
    } catch (err: any) {
      console.error(`Failed to toggle language ${lang.code}:`, err);
      toast.error(err.message || `Failed to update ${lang.name}`);
    }
  };

  const handleSaveLangTemplate = async (langCode: string) => {
    const templateValue = langTemplateDrafts[langCode] ?? '';
    if (templateValue.trim() && !templateValue.includes('{link}')) {
      toast.error('SMS template must contain {link} placeholder for the survey URL');
      return;
    }
    try {
      await organizationApi.upsertSupportedLanguage(langCode, templateValue.trim() || undefined);
      setOrgLanguages(prev =>
        prev.map(l => l.code === langCode ? { ...l, sms_message_template: templateValue.trim() || undefined } : l)
      );
      toast.success('SMS template saved');
    } catch (err: any) {
      console.error(`Failed to save SMS template for ${langCode}:`, err);
      toast.error(err.message || 'Failed to save SMS template');
    }
  };

  return (
    <ClinicLayout
      title="Clinic Profile"
      subtitle="Update clinic details and contact exclusion settings."
    >
      <Card className="mb-4">
        <Box p="4">
          <Flex align="center" justify="between" gap="3" wrap="wrap">
            <Heading size="4">Clinic Information</Heading>
            <Flex gap="2">
              <Button variant="soft" onClick={loadSettings} disabled={loading}>
                <ReloadIcon className={loading ? 'animate-spin' : ''} />
                Refresh
              </Button>
              <Button variant="solid" onClick={handleSaveProfile} disabled={saving}>
                <CheckIcon />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Flex>
          </Flex>

          {error && (
            <Text color="red" size="2" className="mt-3 block">{error}</Text>
          )}

          <Flex direction="column" gap="3" className="mt-4">
            <Box>
              <Text size="2" weight="medium" className="mb-2 block">Clinic name</Text>
              <TextField.Root>
                <TextField.Input
                  placeholder="Clinic name"
                  value={name}
                  onChange={(e) => setName(e.currentTarget.value)}
                />
              </TextField.Root>
            </Box>
            <Box>
              <Text size="2" weight="medium" className="mb-2 block">Logo URL or path</Text>
              <TextField.Root>
                <TextField.Input
                  placeholder="https://example.com/logo.png"
                  value={logoPath}
                  onChange={(e) => setLogoPath(e.currentTarget.value)}
                />
              </TextField.Root>
              <Text size="1" color="gray" className="mt-1 block">
                Use a URL or storage path. File uploads are managed separately. Recommended: PNG with transparent background, ~300x100 px (max 600 px wide) — appears on printed care plans.
              </Text>
            </Box>
            <Box>
              <Text size="2" weight="medium" className="mb-2 block">SMS Phone Number</Text>
              <TextField.Root>
                <TextField.Input
                  placeholder="+12125551234 or 2125551234"
                  value={smsPhoneNumber}
                  onChange={(e) => setSmsPhoneNumber(e.currentTarget.value)}
                  // PHA-1202: a RingCentral clinic's number must be an SMS number of its own
                  // RingCentral account, checked only on the Phanova admin page.
                  disabled={smsProvider === 'ringcentral'}
                />
              </TextField.Root>
              <Text size="1" color="gray" className="mt-1 block">
                {smsProvider === 'ringcentral' ? (
                  <>
                    Used as sender for outbound SMS. Texts are sent through{' '}
                    <strong>RingCentral</strong>{ringcentralAccount ? <> (account <strong>{ringcentralAccount}</strong>)</> : null},
                    from a number on your RingCentral account. Contact Phanova support to change the number or the SMS system.
                  </>
                ) : (
                  <>
                    Used as sender for outbound SMS. Texts are sent through <strong>Twilio</strong>, so this
                    must be a number on Phanova's Twilio account. Contact Phanova support to change the SMS system.
                  </>
                )}
              </Text>
            </Box>
            <Box>
              <Text size="2" weight="medium" className="mb-2 block">SMS Message Template</Text>
              <TextArea
                placeholder="Phanova: Your care team at the clinic asked us to check in with you as part of your Chronic Care Management program: {link}"
                value={smsMessageTemplate}
                onChange={(e) => setSmsMessageTemplate(e.currentTarget.value)}
                rows={3}
              />
              <Text size="1" color="gray" className="mt-1 block">
                Customize the SMS text sent to patients. Use {'{link}'} where the survey URL should appear. Leave blank for the default message.
              </Text>
              {smsMessageTemplate.trim() && (
                <Box className="mt-2 p-2 rounded" style={{ backgroundColor: 'var(--gray-3)' }}>
                  <Text size="1" weight="medium" className="mb-1 block">Preview:</Text>
                  <Text size="1" color="gray">
                    {smsMessageTemplate.replace('{link}', 'https://app.phanova.com/s/abc123')}
                  </Text>
                </Box>
              )}
            </Box>
            <Box>
              {/* PHA-871: per-clinic SMS body for the "Share RPM readings" link */}
              <Text size="2" weight="medium" className="mb-2 block">RPM Readings SMS Template</Text>
              <TextArea
                placeholder="Dear {clinic_name} patient, your remote monitoring readings for {start_date}–{end_date} are ready. You can view or download them here: {link}"
                value={rpmReadingsSmsTemplate}
                onChange={(e) => setRpmReadingsSmsTemplate(e.currentTarget.value)}
                rows={3}
              />
              <Text size="1" color="gray" className="mt-1 block">
                Customize the SMS text sent when sharing device readings with a patient. Use {'{link}'} for the report URL (required), and optionally {'{clinic_name}'}, {'{start_date}'}, {'{end_date}'}. Leave blank for the default message.
              </Text>
              {rpmReadingsSmsTemplate.trim() && (
                <Box className="mt-2 p-2 rounded" style={{ backgroundColor: 'var(--gray-3)' }}>
                  <Text size="1" weight="medium" className="mb-1 block">Preview:</Text>
                  <Text size="1" color="gray">
                    {rpmReadingsSmsTemplate
                      .replace('{clinic_name}', name || 'Your Clinic')
                      .replace('{start_date}', 'May 1, 2026')
                      .replace('{end_date}', 'May 31, 2026')
                      .replace('{link}', 'https://app.phanova.com/rpm-report/abc123')}
                  </Text>
                </Box>
              )}
            </Box>
            <Box>
              {/* PHA-1010: auto-reply to inbound patient texts */}
              <Text size="2" weight="medium" className="mb-2 block">Auto-Reply to Inbound Texts</Text>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={smsAutoReplyEnabled}
                  onChange={(e) => setSmsAutoReplyEnabled(e.target.checked)}
                  className="w-4 h-4"
                />
                <Text size="2">Reply automatically when a patient texts this number</Text>
              </label>
              <Text size="1" color="gray" className="mt-1 block">
                Patients often reply to our messages. This number is not monitored, so we text back
                once per patient per day pointing them to your clinic. Messages containing STOP, START
                or HELP are never auto-replied to — Twilio answers those itself.
              </Text>
            </Box>
            <Box>
              <Text size="2" weight="medium" className="mb-2 block">Auto-Reply Callback Phone Number</Text>
              <TextField.Root>
                <TextField.Input
                  placeholder="+12125551234 or 2125551234"
                  value={smsAutoReplyPhoneNumber}
                  onChange={(e) => setSmsAutoReplyPhoneNumber(e.currentTarget.value)}
                />
              </TextField.Root>
              <Text size="1" color="gray" className="mt-1 block">
                The number patients should call — usually your front desk. Not the SMS sender or Voice AI
                number. Leave blank and the reply tells patients to call their clinic without naming a number.
              </Text>
            </Box>
            <Box>
              <Text size="2" weight="medium" className="mb-2 block">Auto-Reply Message (optional)</Text>
              <TextArea
                placeholder={`We can't reply to texts sent to this number. For clinic questions call {clinic_phone}. Emergency? Call 911.`}
                value={smsAutoReplyTemplate}
                onChange={(e) => setSmsAutoReplyTemplate(e.currentTarget.value)}
                rows={3}
              />
              <Text size="1" color="gray" className="mt-1 block">
                Leave blank for the default above. Use {'{clinic_phone}'} (required) and optionally {'{clinic_name}'}.
                Keep it under 160 characters so it sends as a single message.
              </Text>
              <Box className="mt-2 p-2 rounded" style={{ backgroundColor: 'var(--gray-3)' }}>
                <Text size="1" weight="medium" className="mb-1 block">Preview:</Text>
                <Text size="1" color="gray">
                  {(smsAutoReplyPhoneNumber.trim()
                    ? (smsAutoReplyTemplate.trim() ||
                       "We can't reply to texts sent to this number. For clinic questions call {clinic_phone}. Emergency? Call 911.")
                    : "We can't reply to texts sent to this number. Please call your clinic directly for questions. Emergency? Call 911.")
                    .replace('{clinic_phone}', formatPhoneForDisplay(smsAutoReplyPhoneNumber))
                    .replace('{clinic_name}', name || 'your clinic')}
                </Text>
              </Box>
            </Box>
            <Box>
              <Text size="2" weight="medium" className="mb-2 block">Welcome SMS</Text>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={welcomeSmsEnabled}
                  onChange={(e) => setWelcomeSmsEnabled(e.target.checked)}
                  className="w-4 h-4"
                />
                <Text size="2">Send welcome SMS when patient consents</Text>
              </label>
              <Text size="1" color="gray" className="mt-1 block">
                When enabled, a welcome SMS is sent after the patient completes onboarding consent.
              </Text>
            </Box>
            <Box>
              <Text size="2" weight="medium" className="mb-2 block">Welcome SMS Template (optional)</Text>
              <TextArea
                placeholder={`Hi {first_name}, you've been enrolled in the Chronic Care Program through your clinic, {clinic_name}. We'll be in touch soon to support your care.`}
                value={welcomeSmsTemplate}
                onChange={(e) => setWelcomeSmsTemplate(e.currentTarget.value)}
                rows={3}
              />
              <Text size="1" color="gray" className="mt-1 block">
                Customize the welcome message. Use {'{first_name}'} and {'{clinic_name}'} as placeholders.
                Leave blank to use the default message.
              </Text>
              {welcomeSmsTemplate.trim() && (
                <Box className="mt-2 p-2 rounded" style={{ backgroundColor: 'var(--gray-3)' }}>
                  <Text size="1" weight="medium" className="mb-1 block">Preview:</Text>
                  <Text size="1" color="gray">
                    {welcomeSmsTemplate
                      .replace('{first_name}', 'Jane')
                      .replace('{clinic_name}', 'Your Clinic')}
                  </Text>
                </Box>
              )}
            </Box>
            <Box>
              <Text size="2" weight="medium" className="mb-2 block">Voice AI Phone Number</Text>
              <TextField.Root>
                <TextField.Input
                  placeholder="+12125551234 or 2125551234"
                  value={voicePhoneNumber}
                  onChange={(e) => setVoicePhoneNumber(e.currentTarget.value)}
                />
              </TextField.Root>
              <Text size="1" color="gray" className="mt-1 block">
                Used as caller ID for AI voice calls. Must be registered with VAPI.
              </Text>
            </Box>
            <Box>
              <Text size="2" weight="medium" className="mb-2 block">Voice AI Contact Method</Text>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={voiceEnabled}
                  onChange={(e) => setVoiceEnabled(e.target.checked)}
                  className="w-4 h-4"
                />
                <Text size="2">Enable Voice AI for this clinic</Text>
              </label>
              <Text size="1" color="gray" className="mt-1 block">
                When enabled, patients can be contacted via automated Voice AI phone calls in addition to SMS.
              </Text>
            </Box>
            <Box>
              <Text size="2" weight="medium" className="mb-2 block">Domain</Text>
              <Flex align="center" gap="2">
                <Badge color="gray">{domain || '—'}</Badge>
                <Text size="1" color="gray">Contact support to change</Text>
              </Flex>
            </Box>
          </Flex>
        </Box>
      </Card>

      <OnboardingScriptEditor
        savedTemplate={onboardingScriptTemplate}
        clinicName={name}
        onSaved={setOnboardingScriptTemplate}
      />

      <ConsentSettingsEditor
        english={consentSettings}
        languages={orgLanguages}
        clinicName={name}
        onSaved={(language, saved) => {
          if (language === 'en-US') {
            setConsentSettings(saved);
          } else {
            setOrgLanguages((prev) => prev.map((l) => (l.code === language ? { ...l, ...saved } : l)));
          }
        }}
      />

      <Card className="mb-4">
        <Box p="4">
          <Heading size="4" className="mb-3">Supported Patient Languages</Heading>
          <Text size="2" color="gray" className="mb-3 block">
            Enable additional languages for patient surveys. Each language can have its own SMS message template.
            English (en-US) is always supported by default.
          </Text>

          <Flex direction="column" gap="4">
            {ALL_LANGUAGES.map((lang) => {
              const isEnabled = orgLanguages.some(l => l.code === lang.code);
              const templateDraft = langTemplateDrafts[lang.code] ?? '';
              return (
                <Box key={lang.code} className="p-3 rounded" style={{ backgroundColor: 'var(--gray-2)' }}>
                  <Flex align="center" justify="between" gap="3">
                    <Flex align="center" gap="2">
                      <Switch
                        checked={isEnabled}
                        onCheckedChange={(checked) => handleToggleLanguage(lang, checked)}
                      />
                      <Text size="2" weight="medium">{lang.name}</Text>
                      <Text size="1" color="gray">({lang.native_name})</Text>
                      <Badge color="gray" size="1">{lang.code}</Badge>
                    </Flex>
                  </Flex>

                  {isEnabled && (
                    <Box className="mt-3">
                      <Text size="2" weight="medium" className="mb-2 block">
                        SMS Message Template ({lang.name})
                      </Text>
                      <TextArea
                        placeholder={`Enter the SMS message template in ${lang.name}. Use {link} for the survey URL.`}
                        value={templateDraft}
                        onChange={(e) =>
                          setLangTemplateDrafts(prev => ({ ...prev, [lang.code]: e.currentTarget.value }))
                        }
                        onBlur={() => handleSaveLangTemplate(lang.code)}
                        rows={3}
                      />
                      <Text size="1" color="gray" className="mt-1 block">
                        Use {'{link}'} where the survey URL should appear. Saved automatically on blur.
                      </Text>
                      {templateDraft.trim() && (
                        <Box className="mt-2 p-2 rounded" style={{ backgroundColor: 'var(--gray-3)' }}>
                          <Text size="1" weight="medium" className="mb-1 block">Preview:</Text>
                          <Text size="1" color="gray">
                            {templateDraft.replace('{link}', 'https://app.phanova.com/s/abc123')}
                          </Text>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Flex>
        </Box>
      </Card>

      <Card className="mb-4">
        <Box p="4">
          <Heading size="4" className="mb-3">TelliHealth Webhook</Heading>
          <Text size="2" color="gray" className="mb-3 block">
            Configure the webhook token from your TelliHealth dashboard. Leave empty if TelliHealth is not used.
          </Text>

          <Box className="mb-3">
            <Text size="2" weight="medium" className="mb-2 block">Webhook Token</Text>
            <TextField.Root>
              <TextField.Input
                placeholder="Paste your TelliHealth webhook token here"
                value={tellihealthWebhookToken}
                onChange={(e) => setTellihealthWebhookToken(e.currentTarget.value)}
              />
            </TextField.Root>
            <Text size="1" color="gray" className="mt-1 block">
              This token is used to authenticate incoming webhook requests from TelliHealth. Clear the field to disable webhook authentication.
            </Text>
          </Box>

          {webhookUrl && (
            <Box className="mb-3">
              <Text size="2" weight="medium" className="mb-2 block">Webhook URLs</Text>
              <Flex direction="column" gap="2">
                <Flex align="center" gap="2">
                  <Text size="1" weight="medium" style={{ minWidth: 70 }}>Readings:</Text>
                  <Badge color="gray" style={{ fontFamily: 'monospace' }}>{window.location.origin}{webhookUrl}</Badge>
                  <Button variant="soft" size="1" onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}${webhookUrl}`);
                    toast.success('URL copied');
                  }}>Copy</Button>
                </Flex>
                {eventsUrl && (
                  <Flex align="center" gap="2">
                    <Text size="1" weight="medium" style={{ minWidth: 70 }}>Events:</Text>
                    <Badge color="gray" style={{ fontFamily: 'monospace' }}>{window.location.origin}{eventsUrl}</Badge>
                    <Button variant="soft" size="1" onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}${eventsUrl}`);
                      toast.success('URL copied');
                    }}>Copy</Button>
                  </Flex>
                )}
              </Flex>
              <Text size="1" color="gray" className="mt-2 block">
                Configure these URLs in your TelliHealth dashboard with the token above as the x-api-key header.
              </Text>
            </Box>
          )}
        </Box>
      </Card>

      <Card>
        <Box p="4">
          <Heading size="4" className="mb-3">Contact Exclusion Settings</Heading>
          <Text size="2" color="gray" className="mb-3 block">
            Choose the weekdays and date ranges when patients should not be contacted.
          </Text>

          <Box className="mb-4">
            <Text size="2" weight="medium" className="mb-2 block">Excluded weekdays</Text>
            <Flex gap="2" wrap="wrap">
              {WEEKDAYS.map((day) => {
                const selected = weekdaysDraft.includes(day);
                return (
                  <Button
                    key={day}
                    size="1"
                    variant={selected ? 'solid' : 'soft'}
                    color={selected ? 'orange' : 'gray'}
                    onClick={() => toggleWeekday(day)}
                  >
                    {day}
                  </Button>
                );
              })}
            </Flex>
            <Flex className="mt-3">
              <Button variant="solid" onClick={saveWeekdays} disabled={!orgId}>
                Save weekdays
              </Button>
            </Flex>
          </Box>

          <Box>
            <Flex align="center" justify="between" className="mb-2">
              <Text size="2" weight="medium">Excluded date ranges</Text>
              <Button
                variant="soft"
                onClick={() => orgId && refreshExcludedDates(orgId)}
                disabled={!orgId || excludedDatesLoading}
              >
                <ReloadIcon className={excludedDatesLoading ? 'animate-spin' : ''} />
                Refresh
              </Button>
            </Flex>

            <Table.Root variant="surface">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeaderCell>Exclusion</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Reason</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell style={{ width: 120 }}>Actions</Table.ColumnHeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {excludedDates.length === 0 ? (
                  <Table.Row>
                    <Table.Cell colSpan={3}>
                      <Text color="gray">No excluded date ranges configured.</Text>
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  excludedDates.map((row, idx) => (
                    <Table.Row key={`${row.exclusion}-${idx}`}>
                      <Table.Cell>
                        <Badge color="gray">{row.exclusion}</Badge>
                      </Table.Cell>
                      <Table.Cell>
                        <Text size="2">{row.reason || '—'}</Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Button
                          variant="soft"
                          color="red"
                          onClick={() => deleteExcludedDate(row.exclusion)}
                        >
                          <TrashIcon />
                          Delete
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  ))
                )}

                <Table.Row>
                  <Table.Cell>
                    <TextField.Root>
                      <TextField.Input
                        placeholder="[2026-04-01,2026-04-10)"
                        value={dateDraft.exclusion}
                        onChange={(e) =>
                          setDateDraft((prev) => ({ ...prev, exclusion: e.currentTarget.value }))
                        }
                      />
                    </TextField.Root>
                  </Table.Cell>
                  <Table.Cell>
                    <TextField.Root>
                      <TextField.Input
                        placeholder="Reason (optional)"
                        value={dateDraft.reason}
                        onChange={(e) =>
                          setDateDraft((prev) => ({ ...prev, reason: e.currentTarget.value }))
                        }
                      />
                    </TextField.Root>
                  </Table.Cell>
                  <Table.Cell>
                    <Button variant="solid" onClick={addExcludedDate} disabled={!orgId}>
                      Add
                    </Button>
                  </Table.Cell>
                </Table.Row>
              </Table.Body>
            </Table.Root>
          </Box>
        </Box>
      </Card>
    </ClinicLayout>
  );
};

export default ClinicProfilePage;
